import type { ChartPeriod, ChartDimensions, GroupedMetric, TooltipData } from '../../types';
import { CHART_CONSTANTS } from '../../constants';
import { formatCompactCurrency } from '../../utils';

interface BaseLineSeriesProps {
  /** Callback when hovering over a data point */
  onHover?: (data: TooltipData | null) => void;
}

interface StandardLineSeriesProps extends BaseLineSeriesProps {
  mode: 'standard';
  periods: ChartPeriod[];
  dimensions: ChartDimensions;
  currentTotals: Map<string, number>;
  comparisonTotals: Map<string, number>;
  hasComparison: boolean;
}

interface MetricLineSeriesProps extends BaseLineSeriesProps {
  mode: 'metric';
  periods: ChartPeriod[];
  dimensions: ChartDimensions;
  allGroupedMetrics: GroupedMetric[];
}

type LineSeriesProps = StandardLineSeriesProps | MetricLineSeriesProps;

/**
 * Renders line chart series with data points.
 * Supports standard mode (current vs comparison) and metric mode (multiple metrics).
 * Includes hover interaction for tooltip display.
 */
export function LineSeries(props: LineSeriesProps) {
  const { periods, dimensions, onHover } = props;
  const { getX, getY } = dimensions;
  const { COLORS } = CHART_CONSTANTS;

  // Generate line path for a series
  const generateLinePath = (values: Map<string, number>, axisMaxValue?: number) => {
    const points = periods.map((p, i) => {
      const value = values.get(p.key) || 0;
      return `${getX(i)},${getY(value, axisMaxValue)}`;
    });
    return `M ${points.join(' L ')}`;
  };

  if (props.mode === 'metric') {
    const { allGroupedMetrics } = props;

    return (
      <>
        {allGroupedMetrics.map((metric) => (
          <g key={metric.key}>
            <path
              d={generateLinePath(metric.values, metric.groupMaxValue)}
              fill="none"
              stroke={metric.color}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* Data points with value labels */}
            {periods.map((p, i) => {
              const value = metric.values.get(p.key) || 0;
              const cx = getX(i);
              const cy = getY(value, metric.groupMaxValue);

              const handleMouseEnter = () => {
                onHover?.({
                  x: cx,
                  y: cy - 12,
                  periodKey: p.key,
                  periodLabel: p.label,
                  currentValue: value,
                  metricKey: metric.key,
                  isCurrency: metric.isCurrency,
                });
              };

              const handleMouseLeave = () => {
                onHover?.(null);
              };

              return (
                <g key={`${metric.key}-${p.key}`}>
                  <circle
                    cx={cx}
                    cy={cy}
                    r="5"
                    fill={metric.color}
                    stroke="#232429"
                    strokeWidth="2"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onFocus={handleMouseEnter}
                    onBlur={handleMouseLeave}
                    tabIndex={0}
                    role="graphics-symbol"
                    aria-label={`${p.label}: ${metric.isCurrency ? formatCompactCurrency(value) : Math.round(value).toString()}`}
                  />
                  {/* Value label above point (only if single metric) */}
                  {allGroupedMetrics.length === 1 && (
                    <text
                      x={cx}
                      y={cy - 10}
                      textAnchor="middle"
                      fontSize="10"
                      fill={metric.color}
                      fontWeight="600"
                      style={{ pointerEvents: 'none' }}
                    >
                      {metric.isCurrency ? formatCompactCurrency(value) : Math.round(value).toString()}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        ))}
      </>
    );
  }

  // Standard mode
  const { currentTotals, comparisonTotals, hasComparison } = props;

  return (
    <>
      {/* Comparison line (dashed) */}
      {hasComparison && (
        <g>
          <path
            d={generateLinePath(comparisonTotals)}
            fill="none"
            stroke={COLORS.COMPARISON}
            strokeWidth="2"
            strokeDasharray="6,4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {periods.map((p, i) => {
            const value = comparisonTotals.get(p.key) || 0;
            return (
              <circle
                key={`comp-${p.key}`}
                cx={getX(i)}
                cy={getY(value)}
                r="4"
                fill={COLORS.COMPARISON}
                stroke="#232429"
                strokeWidth="2"
              />
            );
          })}
        </g>
      )}
      {/* Current period line (solid) */}
      <path
        d={generateLinePath(currentTotals)}
        fill="none"
        stroke={COLORS.CURRENT}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {periods.map((p, i) => {
        const currentValue = currentTotals.get(p.key) || 0;
        const compValue = comparisonTotals.get(p.key) || 0;
        const cx = getX(i);
        const cy = getY(currentValue);

        const handleMouseEnter = () => {
          onHover?.({
            x: cx,
            y: cy - 12,
            periodKey: p.key,
            periodLabel: p.label,
            currentValue,
            comparisonValue: hasComparison ? compValue : undefined,
            isCurrency: true,
          });
        };

        const handleMouseLeave = () => {
          onHover?.(null);
        };

        return (
          <circle
            key={`current-${p.key}`}
            cx={cx}
            cy={cy}
            r="5"
            fill={COLORS.CURRENT}
            stroke="#232429"
            strokeWidth="2"
            style={{ cursor: 'pointer' }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onFocus={handleMouseEnter}
            onBlur={handleMouseLeave}
            tabIndex={0}
            role="graphics-symbol"
            aria-label={`${p.label}: ${formatCompactCurrency(currentValue)}${hasComparison && compValue > 0 ? ` (${currentValue >= compValue ? '+' : ''}${((currentValue - compValue) / compValue * 100).toFixed(0)}%)` : ''}`}
          />
        );
      })}
    </>
  );
}
